import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { StatusDot } from '../ui/status-dot';
import { Badge } from '../ui/badge';

/**
 * One row of an agent-thread readout: a live status dot, the thread name, an
 * optional attention badge, and a one-line headline of where the thread is.
 * This is the form Chief's `list_agents` digest established; it lives here so
 * every surface that lists agent threads (Chief's panel, the composer's
 * activity bar) renders them identically.
 */
export interface AgentThreadListEntry {
  id: string;
  name: string;
  turnState: 'working' | 'idle';
  mark?: 'blocked' | 'in_review' | 'completed' | 'failed';
  /** One-line summary of where the thread is. */
  headline: string;
}

const MARK_LABELS: Record<NonNullable<AgentThreadListEntry['mark']>, string> = {
  blocked: 'Blocked',
  in_review: 'In review',
  completed: 'Done',
  failed: 'Failed',
};

/**
 * Rows shown before the list collapses. A scan of a busy fleet used to land
 * as a wall of rows nobody asked for; the preview keeps the threads that need
 * the user and leaves the rest one click away.
 */
export const THREAD_PREVIEW_ROWS = 3;

/** Blocked first, then live work, then review, failures, and rest. */
function attentionRank(entry: AgentThreadListEntry): number {
  if (entry.mark === 'blocked') return 0;
  if (entry.turnState === 'working') return 1;
  if (entry.mark === 'in_review') return 2;
  if (entry.mark === 'failed') return 3;
  return 4;
}

/**
 * Order a list so the preview shows what matters. The sort is stable, so the
 * caller's own order (typically newest-first) survives inside each band.
 */
export function orderByAttention(entries: AgentThreadListEntry[]): AgentThreadListEntry[] {
  return [...entries].sort((a, b) => attentionRank(a) - attentionRank(b));
}

export function threadTone(entry: AgentThreadListEntry): {
  tone: 'success' | 'warning' | 'error' | 'muted';
  pulse: boolean;
} {
  if (entry.mark === 'blocked') return { tone: 'warning', pulse: false };
  if (entry.mark === 'failed') return { tone: 'error', pulse: false };
  if (entry.turnState === 'working') return { tone: 'success', pulse: true };
  return { tone: 'muted', pulse: false };
}

interface AgentThreadRowProps {
  entry: AgentThreadListEntry;
  /** When provided, the row becomes a button that jumps into the thread. */
  onOpen?: (id: string) => void;
  withTopBorder?: boolean;
}

/** A single thread row; exported so composed surfaces can reuse the form. */
export function AgentThreadRow({ entry, onOpen, withTopBorder }: AgentThreadRowProps) {
  const { tone, pulse } = threadTone(entry);
  const content = (
    <>
      <StatusDot tone={tone} pulse={pulse} size="sm" />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-xs font-medium">{entry.name}</span>
          {entry.mark && (
            <Badge
              variant={entry.mark === 'blocked' || entry.mark === 'failed' ? 'outline' : 'subtle'}
              className="px-1.5 py-0 text-3xs"
            >
              {MARK_LABELS[entry.mark]}
            </Badge>
          )}
        </span>
        <span className="block truncate text-xs text-muted-foreground">{entry.headline}</span>
      </span>
    </>
  );
  const rowClass = cn(
    'flex w-full items-center gap-2 px-2.5 py-2 text-left',
    withTopBorder && 'border-t border-border/40',
  );
  return onOpen ? (
    <button
      type="button"
      onClick={() => onOpen(entry.id)}
      className={cn(rowClass, 'group transition-colors hover:bg-accent/50')}
    >
      {content}
      <ChevronRight className="icon-xs shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5" />
    </button>
  ) : (
    <div className={rowClass}>{content}</div>
  );
}

interface AgentThreadListProps {
  entries: AgentThreadListEntry[];
  /** When provided, rows become buttons that jump into the thread. */
  onOpenThread?: (agentId: string) => void;
  /** Noun in the "Show all N ..." expander label. */
  itemNoun?: string;
  testId?: string;
  toggleTestId?: string;
  className?: string;
}

/**
 * An inline agent-thread readout: one row per thread, ordered by attention
 * and capped at the threads that need the user until they ask for the rest.
 * With an `onOpenThread` handler every row jumps straight into that thread —
 * the list is a remote control, not a report.
 */
export function AgentThreadList({
  entries,
  onOpenThread,
  itemNoun = 'threads',
  testId = 'agent-thread-list',
  toggleTestId = 'agent-thread-list-toggle',
  className,
}: AgentThreadListProps) {
  const [expanded, setExpanded] = useState(false);
  const ordered = useMemo(() => orderByAttention(entries), [entries]);
  const visible = expanded ? ordered : ordered.slice(0, THREAD_PREVIEW_ROWS);
  const hiddenCount = ordered.length - visible.length;

  return (
    <div
      data-testid={testId}
      className={cn(
        'overflow-hidden rounded-xl border border-border/60 bg-card/60 shadow-xs',
        className,
      )}
    >
      {visible.map((entry, index) => (
        <AgentThreadRow
          key={entry.id}
          entry={entry}
          onOpen={onOpenThread}
          withTopBorder={index > 0}
        />
      ))}

      {(hiddenCount > 0 || expanded) && (
        <button
          type="button"
          data-testid={toggleTestId}
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
          className="flex w-full items-center gap-1 border-t border-border/40 px-2.5 py-1.5 text-2xs text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
        >
          <ChevronDown className={cn('icon-xs transition-transform', expanded && 'rotate-180')} />
          {expanded ? 'Show fewer' : `Show all ${ordered.length} ${itemNoun}`}
        </button>
      )}
    </div>
  );
}
